import { useEffect } from 'react';
import { Link, useLoaderData } from 'react-router-dom';
import useRequestState from '../hooks/useRequestState';
import PostDebtCards from '../services/adapters/debtCard';
import DebtCard from '../components/debt/debtCard';
import GetLatestSnapshot from '../services/adapters/snapshotResponse';
import { Snapshot } from '../types/core.type';
import { DebtCardInterface } from '../types/debt.type';
import { extendFiltersWithWard } from '../utils/filters';
import SnapshotWardDropdown from '../components/snapshotWardDropdown';
import useSnapshotWardFilters from '../hooks/useSnapshotWardFilters';

export const handle = {
  breadcrumb: () => <Link id="breadcrumb-debt" to="/debt">Return to Debt</Link>,
};

export function loader() {
  const snapshotAdapter = new GetLatestSnapshot();
  return snapshotAdapter.adaptResponse();
}
export default function Debt() {
  const snapshot = useLoaderData() satisfies Snapshot;

  const {
    selectedSnapshot,
    selectedLocalAuthority,
    selectedWard,
    handleSnapshotChange,
    handleLocalAuthorityChange,
    handleWardChange,
  } = useSnapshotWardFilters(snapshot);

  const {
    data: debtCardData,
    handleRequest,
    FallbackWrapper,
  } = useRequestState<DebtCardInterface[]>();

  useEffect(() => {
    if (!selectedSnapshot.value || !snapshot) return;
    const adapter = new PostDebtCards();
    const response = adapter.adaptResponse({ snapshot: selectedSnapshot.value, liftDataId: selectedLocalAuthority.value, wardName: selectedWard.value });
    void handleRequest(response);
  }, [selectedSnapshot, selectedLocalAuthority, selectedWard]);

  return (
    <div className="flex flex-col w-full gap-4 py-5 px-6">
      <header id="debt-page-header" className="flex flex-col gap-2">
        <div className="flex items-center gap-3">
          <div className="text-3xl font-bold text-piptext-default">Debt</div>
        </div>
        <span id="debt-page-description" className="text-pipgrey text-sm font-normal">
          Summary of debt levels, changes over time and emerging risks across different debt types
        </span>

        <SnapshotWardDropdown
          selectedSnapshot={selectedSnapshot || { label: snapshot.formattedSnapshot, value: snapshot.rawSnapshot }}
          selectedWard={selectedWard}
          selectedLocalAuthority={selectedLocalAuthority}
          onSnapshotChange={handleSnapshotChange}
          onWardChange={handleWardChange}
          onLocalAuthorityChange={handleLocalAuthorityChange}
        />
      </header>

      <div id="debt-cards-container" className="grid grid-cols-1 lg:grid-cols-2 gap-4 pb-4">
        <FallbackWrapper loadingMessage="Fetching data...">
          {debtCardData?.map((debtData) => {
            const metricsWithWardFilter = debtData.metrics.map((metric) => ({
              ...metric,
              filters: extendFiltersWithWard(metric.filters, selectedWard.value),
            }));
            return (
              <DebtCard
                key={debtData.displayName}
                displayName={debtData.displayName}
                description={debtData.description}
                metrics={metricsWithWardFilter}
                graphData={debtData.graphData}
                redirectUrl={debtData.redirectUrl}
              />
            );
          })}
        </FallbackWrapper>
      </div>
    </div>
  );
}
